import {
    ArtifactPlace,
    ConsumablePlace,
    CustomPlace, Dialog, Education, GainablePlace, NaturePlace,
    Place,
    PlaceState,
    PlaceType,
    PointerPlace, PortalPlace,
    ResourcePlace,
    RunePlace, ScriptPlace, SkillPlace, TalkerPlace, TeleportDto,
    Tile,
    TurnPlace
} from './types';
import http from '../../axios';
import * as Collections from 'typescript-collections';
import {Balance, Player} from "@/store/player/types";
import PlayerStore from "@/store/player";

export default class PlaceStore {

    private static state: PlaceState = {
        tiles: [],
        places: [],
        educations: [],
        dialogs: [],
        dialogIdsCache: [],
        idsCache: new Collections.Set<number>()
    }

    /**
     * Сервер сменил контекст мимо клиента: ответ диалога с toTile/toRoad/restoreContext не доехал
     * (например, ушёл в отложенную очередь при рассинхроне реплики), а мир уже другой. Признак
     * ловится по contextId из ensure и разбирается сценой - без перезапуска клиент останется
     * со старым фронтиром запроса мест и не увидит ничего, что сервер создал позади него.
     */
    private static pendingContextChange: number | undefined = undefined;
    private static pendingContextCoordinate: number = 0;

    public static consumeContextChange(): number | undefined {
        const coordinate = PlaceStore.pendingContextChange !== undefined ? PlaceStore.pendingContextCoordinate : undefined;
        PlaceStore.pendingContextChange = undefined;
        return coordinate;
    }

    public static getPlacesAll() {
        return PlaceStore.state.places;
    }

    public static getDialogs() {
        return PlaceStore.state.dialogs;
    }

    public static getPlaces(coordFrom: number, coordTo: number) {
        return PlaceStore.state.places.filter(place => place.posY && coordFrom <= place.posY && place.posY < coordTo);
    }

    public static placeExists(id: number) {
        return PlaceStore.state.places.filter(place => place.id === id).length > 0;
    }

    public static getBattleLostPlace() {
        return PlaceStore.state.places.find(place => place.battleLost);
    }

    public static getTiles() {
        return PlaceStore.state.tiles;
    }

    public static getEducations() {
        return PlaceStore.state.educations;
    }

    public static getMinCoordinate(): number {
        return PlayerStore.getPlayer().minCoordinate;
    }

    public static getMaxCoordinate(): number {
        return PlayerStore.getPlayer().maxCoordinate;
    }

    public static CLEAR_ALL_PLACES() {
        PlaceStore.state.tiles = [];
        PlaceStore.state.places = [];
        PlaceStore.state.educations = [];
        PlaceStore.state.dialogs = [];
        PlaceStore.state.dialogIdsCache = [];
        PlaceStore.state.idsCache = new Collections.Set<number>();
    }

    public static CLEAN_UP_DIALOGS() {
        PlaceStore.state.dialogs = [];
        PlaceStore.state.dialogIdsCache = [];
    }

    public static CLEAN_UP_PLACES(maxCoordinate: number) {
        for (let place of PlaceStore.state.places) {
            if (place.posY && place.posY < maxCoordinate) {
                PlaceStore.state.idsCache.remove(place.id);
            }
        }
        PlaceStore.state.places = PlaceStore.state.places.filter(place => place.posY && place.posY >= maxCoordinate);
    }

    public static ADD_PLACES(places: Place[]) {
        for (let place of places) {
            if (!PlaceStore.state.idsCache.contains(place.id)) {
                PlaceStore.state.places.push(place);
                PlaceStore.state.idsCache.add(place.id);
            }
        }
    }

    public static REMOVE_CREATURE(placeId: number) {
        for (let place of PlaceStore.state.places) {
            if (place.id == placeId) {
                place.creatureId = null;
            }
        }
    }

    public static REMOVE_PLACE(placeId: number) {
        PlaceStore.state.places = PlaceStore.state.places.filter(place => place.id !== placeId);
    }

    public static REMOVE_DIALOG(dialogId: string) {
        PlaceStore.state.dialogs = PlaceStore.state.dialogs.filter(d => d.id !== dialogId);
        PlaceStore.state.dialogIdsCache = PlaceStore.state.dialogIdsCache.filter(id => id !== dialogId);
    }

    public static SET_PLACE_BATTLE_LOST(placeId: number) {
        for (let place of PlaceStore.state.places) {
            if (place.id === placeId) {
                place.battleLost = true;
            }
        }
    }

    public static SET_TILES(tiles: Tile[]) {
        PlaceStore.state.tiles = tiles;
    }

    public static SET_EDUCATIONS(educations: Education[]) {
        PlaceStore.state.educations = educations;
    }

    public static ADD_EDUCATIONS(educations: Education[]) {
        for (const e of educations) {
            if (!PlaceStore.state.educations.some(ex => ex.id === e.id)) {
                PlaceStore.state.educations.push(e);
            }
        }
    }

    public static SET_DIALOGS(dialogs: Dialog[]) {
        let coordinate = PlayerStore.getPlayer().coordinate;
        for (let d of dialogs) {
            if (!PlaceStore.state.dialogIdsCache.includes(d.id)) {
                PlaceStore.state.dialogIdsCache.push(d.id);
                PlaceStore.state.dialogs.push(d)
            }
        }
        let GAP = 1500;
        for (let d of PlaceStore.state.dialogs) {
            if (d.y < coordinate - GAP) {
                PlaceStore.state.dialogs.splice(PlaceStore.state.dialogs.indexOf(d), 1);
            }
        }
    }

    private static SET_PLACE_USED(placeId: number) {
        for (let place of PlaceStore.state.places) {
            if (place.id === placeId) {
                if (place instanceof ScriptPlace) {
                    let mp: ScriptPlace = <ScriptPlace>place;
                    mp.used = true;
                }
            }
        }
    }

    private static handlePlaces(places: any): void {
        PlaceStore.ADD_PLACES(PlaceStore.convert(places));
    }

    public static convert(places: any): Place[] {
        let res: Place[] = [];

        for (let place of places) {
            switch (place.placeType) {
                case PlaceType.RESOURCE:
                    let rp: ResourcePlace = Object.assign(new ResourcePlace(), place);
                    res.push(rp);
                    break;
                case PlaceType.CUSTOM:
                    let cp: CustomPlace = Object.assign(new CustomPlace(), place);
                    res.push(cp);
                    break;
                case PlaceType.ARTIFACT:
                    let ap: ArtifactPlace = Object.assign(new ArtifactPlace(), place);
                    res.push(ap);
                    break;
                case PlaceType.RUNE:
                    let r: RunePlace = Object.assign(new RunePlace(), place);
                    res.push(r);
                    break;
                case PlaceType.CONSUMABLE:
                    let cons: ConsumablePlace = Object.assign(new ConsumablePlace(), place);
                    res.push(cons);
                    break;
                case PlaceType.POINTER:
                    let pp: PointerPlace = Object.assign(new PointerPlace(), place);
                    res.push(pp);
                    break;
                case PlaceType.TURN:
                    let tp: TurnPlace = Object.assign(new TurnPlace(), place);
                    res.push(tp);
                    break;
                case PlaceType.GAINABLE:
                    let gp: GainablePlace = Object.assign(new GainablePlace(), place);
                    res.push(gp);
                    break;
                case PlaceType.SCRIPT:
                    let scrp: ScriptPlace = Object.assign(new ScriptPlace(), place);
                    res.push(scrp);
                    break;
                case PlaceType.PORTAL:
                    let ppp: PortalPlace = Object.assign(new PortalPlace(), place);
                    res.push(ppp);
                    break;
                case PlaceType.SKILL:
                    let sp: SkillPlace = Object.assign(new SkillPlace(), place);
                    res.push(sp);
                    break;
                case PlaceType.TALKER:
                    let t: TalkerPlace = Object.assign(new TalkerPlace(), place);
                    res.push(t);
                    break;
                case PlaceType.NATURE:
                    let nat: NaturePlace = Object.assign(new NaturePlace(), place);
                    res.push(nat);
                    break;
            }
        }

        return res;
    }

    public static fetchPlaces(playerCoordinate: number, coordFrom: number, coordTo: number, shouldCommit?: () => boolean) {
        PlaceStore.CLEAN_UP_PLACES(playerCoordinate - 1000);
        return http.get('/api/place/v3/ensure', {params: {coordFrom: coordFrom, coordTo: coordTo}})
            .then(r => r.data)
            .then(road => {
                if (shouldCommit && !shouldCommit()) return;

                const player = PlayerStore.getPlayer();
                if (road.contextId !== undefined && road.contextId !== null
                    && player && player.contextId !== undefined && player.contextId !== road.contextId) {
                    // contextId правим сразу, иначе признак поднимется на каждом следующем ensure
                    player.contextId = road.contextId;
                    PlaceStore.pendingContextChange = road.contextId;
                    PlaceStore.pendingContextCoordinate = road.coordinate;
                }

                PlaceStore.handlePlaces(road.places);

                let tiles = [];
                for (let tile of road.tiles) {
                    let t: Tile = Object.assign(new Tile(), tile);
                    tiles.push(t);
                }
                PlaceStore.SET_TILES(tiles);
                PlaceStore.SET_EDUCATIONS(road.educations);
                PlaceStore.SET_DIALOGS(road.dialogs);
                PlayerStore.SET_MIN_COORD(road.minCoordinate);
                PlayerStore.SET_MAX_COORD(road.maxCoordinate);
                PlayerStore.SET_CONTROL_TYPE(road.controlType);
                PlayerStore.SET_ROAD_EFFECT(road.effect)
                PlayerStore.SET_LOAD_SOUNDS(road.loadSounds);
            });
    }

    public static fetchSpecialPlaces() {
        return http.get('/api/place/special/get');
    }

    public static takeResource(placeId: number): Promise<void> {
        return http.get('/api/resourcePlace/' + placeId + '/take')
            .then(r => r.data)
            .then((balanceObj) => {
                let balance: Balance = Object.assign(new Balance(), balanceObj);
                PlayerStore.SET_BALANCE(balance, false);
                PlaceStore.REMOVE_PLACE(placeId);
            });
    }

    public static takeSkill(place: SkillPlace): Promise<void> {
        PlaceStore.REMOVE_PLACE(place.id);
        return http.get('/api/skill/place/' + place.id + '/take');
    }

    public static takeGainable(placeId: number): Promise<void> {
        return http.post('/api/place/gainable/' + placeId + '/gain')
            .then(r => r.data)
            .then((playerObj) => {
                let player = Object.assign(new Player(), playerObj);
                PlayerStore.SET_PLAYER(player);
            });
    }

    public static turn(placeId: number): Promise<void> {
        return http.post('/api/turn/v2/' + placeId + '/use')
            .then(r => r.data)
            .then((playerObj) => {
                let player = Object.assign(new Player(), playerObj);
                PlayerStore.SET_PLAYER_SILENT_SKIP_CR(player);
                PlaceStore.CLEAR_ALL_PLACES();
            });
    }

    public static back(contextId: number): Promise<void> {
        return http.post('/api/turn/v2/back/' + contextId)
            .then(r => r.data)
            .then((playerObj) => {
                let player = Object.assign(new Player(), playerObj);
                PlayerStore.SET_PLAYER_SILENT_SKIP_CR(player);
                PlaceStore.CLEAR_ALL_PLACES();
            });
    }

    public static customBack(contextId: number, placeId: number): Promise<void> {
        return http.post('/api/turn/v2/custom/back/' + String(contextId) + '/' + String(placeId))
            .then(r => r.data)
            .then((playerObj) => {
                let player = Object.assign(new Player(), playerObj);
                PlayerStore.SET_PLAYER_SILENT_SKIP_CR(player);
                PlaceStore.CLEAR_ALL_PLACES();
            });
    }

    public static setUsed(placeId: number) {
        PlaceStore.SET_PLACE_USED(placeId);
    }

    public static fetchTeleportInfo(placeId: number): Promise<TeleportDto> {
        return http.get('/api/teleport/' + String(placeId) + '/get')
            .then(r => r.data)
            .then(resObj => {
                return Object.assign(new TeleportDto(), resObj);
            });
    }

    public static useTeleport(placeId: number, roadId: string): Promise<Player> {
        return http.post('/api/teleport/' + String(placeId) + '/use/' + roadId)
            .then(r => r.data)
            .then(playerObj => {
                let player = Object.assign(new Player(), playerObj);
                PlayerStore.SET_PLAYER_SILENT(player);
                PlaceStore.CLEAR_ALL_PLACES();
                return player;
            });
    }

    public static createMarkerFromTalks(placeId: number, marker: string): Promise<void> {
        return http.post('/api/customPlace/scenario/marker', null, {params: {placeId: placeId, marker: marker}})
    }
}
